/**
 * Turns an account's statement coverage into the segments the CoverageBar
 * draws — covered stretches and the holes between them.
 */
import type { Account } from '@/generated/types'
import type { CoverageSegment } from '@/components/charts/Bars'
import { parseDate } from './format'

const DAY = 86_400_000

export function coverageSegments(coverage: Account['coverage']): CoverageSegment[] {
  if (!coverage?.from || !coverage.to) return []
  const start = parseDate(coverage.from).getTime()
  const end = parseDate(coverage.to).getTime()
  const span = Math.max(end - start, DAY)

  const gaps = [...(coverage.gaps ?? [])]
    .map((gap) => ({ from: parseDate(gap.from).getTime(), to: parseDate(gap.to).getTime() }))
    .filter((gap) => gap.to > start && gap.from < end)
    .sort((a, b) => a.from - b.from)

  const segments: CoverageSegment[] = []
  let cursor = start
  for (const gap of gaps) {
    const gapStart = Math.max(gap.from, start)
    if (gapStart > cursor) segments.push({ ratio: (gapStart - cursor) / span, covered: true })
    const gapEnd = Math.min(gap.to, end)
    segments.push({ ratio: Math.max(gapEnd - gapStart, 0) / span, covered: false })
    cursor = gapEnd
  }
  if (cursor < end) segments.push({ ratio: (end - cursor) / span, covered: true })
  return segments
}

export function hasGaps(account: Account): boolean {
  return (account.coverage?.gaps?.length ?? 0) > 0
}
